import { useState, useRef } from "react";
import "../style/Kontakt.css";
import { Col, Row, Container, Button, Form } from "react-bootstrap";
import kontaktPic from "./images/Kontakt_pic.png";

const initialFormState = {
  unternehmen: "",
  name: "",
  telefon: "",
  email: "",
  nachricht: "",
  datenschutz: false,
};

const MAX_FILE_SIZE_MB = 5;

function Kontakt(props) {
  const [formData, setFormData] = useState(initialFormState);
  const [lebenslauf, setLebenslauf] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const apiUrl = process.env.REACT_APP_KONTAKT_API_URL || "/api/kontakt";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTelefonChange = (event) => {
    const sanitized = event.target.value.replace(/\D/g, "");
    setFormData((prev) => ({
      ...prev,
      telefon: sanitized,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setStatus({ type: "error", message: "Bitte laden Sie nur PDF-Dateien hoch." });
      event.target.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setStatus({ type: "error", message: `Die Datei darf nicht größer als ${MAX_FILE_SIZE_MB} MB sein.` });
      event.target.value = "";
      return;
    }
    setStatus({ type: "", message: "" });
    setLebenslauf(file);
  };

  const handleRemoveFile = () => {
    setLebenslauf(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email.trim()) {
      setStatus({ type: "error", message: "Bitte geben Sie eine gültige E-Mail-Adresse an." });
      return;
    }

    if (!formData.datenschutz) {
      setStatus({ type: "error", message: "Bitte akzeptieren Sie die Datenschutzerklärung." });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));
      if (lebenslauf) body.append("lebenslauf", lebenslauf);

      const response = await fetch(apiUrl, {
        method: "POST",
        body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Request failed");
      }

      setStatus({ type: "success", message: "Danke! Ihre Nachricht wurde gesendet." });
      setFormData(initialFormState);
      setLebenslauf(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      setStatus({ type: "error", message: "Nachricht konnte nicht gesendet werden. Bitte später erneut versuchen." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero image with overlay text and CTA */}
      <div className="medway-hero kontakt-hero">
        <img
          src={kontaktPic}
          alt="Kontakt Hero"
          className="medway-hero__image"
        />
        <div className="medway-hero__overlay" />
        <div
          className="medway-hero__content kontakt-hero__content"
        >
          <h1 className="medway-hero__title">Nehmen Sie Kontakt auf</h1>
          <p className="medway-hero__subtitle">Lassen Sie uns in Verbindung treten!</p>
          <div>
            <Button
              href="#kontaktformular"
              variant="outline-light"
              size="lg"
              style={{
                padding: "12px 28px",
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "1rem",
                backgroundColor: "rgba(255,255,255,0.08)",
                borderWidth: "2px",
              }}
            >
              Zum Kontaktformular
            </Button>
          </div>
        </div>
      </div>

      {/* Kontakt info card */}
      <Container className="my-5 medway-section--center">
        <div
          className="medway-card"
        >
          <h2 style={{ color: "#0f4b78", fontSize: "2rem", fontWeight: 500, marginBottom: "12px" }}>Kontakt</h2>
          <div
            style={{
              width: "220px",
              height: "6px",
              backgroundColor: "#8c1030",
              borderRadius: "4px",
              margin: "0 auto 20px",
            }}
          />
          <p
            style={{
              fontSize: "1.05rem",
              lineHeight: "1.65",
              color: "#3a3a3a",
              maxWidth: "900px",
              margin: "0 auto 32px",
            }}
          >
            Unser freundliches Serviceteam ist jederzeit für Sie erreichbar – telefonisch, per E-Mail oder über unser Kontaktformular. Unabhängig vom gewählten Kontaktweg garantieren wir Ihnen eine schnelle Rückmeldung sowie eine fachkundige und zuverlässige Beratung.
          </p>
          <Row className="g-4 justify-content-center">
            <Col md={6} xs={12}>
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                  padding: "28px 20px",
                  textAlign: "center",
                  border: "1px solid #eef2f7",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M40 16c7 0 12 5 12 12" stroke="#111D4B" strokeWidth="3" strokeLinecap="round" />
                    <path d="M40 22c3.5 0 6 2.5 6 6" stroke="#111D4B" strokeWidth="3" strokeLinecap="round" />
                    <path
                      d="M28.5 18h7c1.2 0 2.2.8 2.4 1.9l1.1 5.6c.2 1-.2 2.1-1 2.7l-3.4 2.6c2 3.6 4.9 6.5 8.5 8.5l2.6-3.3c.6-.8 1.6-1.2 2.7-1l5.5 1.1c1.1.2 1.9 1.2 1.9 2.4v7c0 1.3-1.1 2.4-2.4 2.4C36 47.8 24.2 36 22.1 23.1 21.8 21.1 23.3 19 25.3 19c.1 0 3.2-.9 3.2-.9Z"
                      stroke="#111D4B"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </div>
                <h4 style={{ color: "#8c1030", fontWeight: 600, marginBottom: "6px" }}>Telefon</h4>
                <p style={{ marginBottom: 0 }}>
                  <a
                    href="tel:+21651613741"
                    style={{ color: "#111D4B", fontSize: "1.1rem", fontWeight: 600, textDecoration: "none" }}
                  >
                    +216 51 613 741
                  </a>
                </p>
              </div>
            </Col>
            <Col md={6} xs={12}>
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                  padding: "28px 20px",
                  textAlign: "center",
                  border: "1px solid #eef2f7",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="12" y="18" width="48" height="36" rx="8" stroke="#111D4B" strokeWidth="3" />
                    <path d="M12 22l24 18L60 22" stroke="#111D4B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 30l16 12 16-12" stroke="#111D4B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="48" y="40" width="14" height="14" rx="3" fill="#fff" stroke="#111D4B" strokeWidth="3" />
                    <path d="M50 42h10" stroke="#111D4B" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M50 46h10" stroke="#111D4B" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M50 50h5" stroke="#111D4B" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
                <h4 style={{ color: "#8c1030", fontWeight: 600, marginBottom: "6px" }}>E-Mail</h4>
                <p style={{ marginBottom: 0 }}>
                  <a
                    href="mailto:recruitmentmedway@gmail.com"
                    style={{ color: "#111D4B", fontSize: "1.1rem", fontWeight: 600, textDecoration: "none" }}
                  >
                    recruitmentmedway@gmail.com
                  </a>
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Kontaktformular */}
      <div id="kontaktformular" className="kontakt-form">
        <div
          className="kontakt-form__inner"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${require("./images/Kontakt_personal.jpg")})`,
          }}
        >
          <div
            className="kontakt-form__card"
          >
            <h2 style={{ fontSize: "2.2rem", fontWeight: 300, marginBottom: "8px" }}>Kontaktformular</h2>
            <p style={{ fontSize: "1.05rem", fontWeight: 300, marginBottom: "20px", color: "#e3e8ff" }}>
              Wir antworten Ihnen so schnell wie möglich!
            </p>
            <Form onSubmit={handleSubmit} noValidate>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group controlId="unternehmen">
                    <Form.Label style={{ color: "#e3e8ff" }}>Unternehmen</Form.Label>
                    <Form.Control
                      type="text"
                      name="unternehmen"
                      value={formData.unternehmen}
                      onChange={handleChange}
                      placeholder="Unternehmensname"
                      style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label style={{ color: "#e3e8ff" }}>Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Name"
                      style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="telefon">
                    <Form.Label style={{ color: "#e3e8ff" }}>Telefon</Form.Label>
                    <Form.Control
                      type="tel"
                      name="telefon"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Telefonnummer"
                      value={formData.telefon}
                      onChange={handleTelefonChange}
                      onKeyDown={(event) => {
                        const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
                        if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group controlId="email">
                    <Form.Label style={{ color: "#e3e8ff" }}>
                      E-Mail <span style={{ color: "#ff8ba7" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="E-Mail"
                      required
                      style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group controlId="nachricht">
                    <Form.Label style={{ color: "#e3e8ff" }}>Nachricht</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="nachricht"
                      value={formData.nachricht}
                      onChange={handleChange}
                      placeholder="Nachricht"
                      style={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Label style={{ color: "#e3e8ff" }}>
                    Bewerbung / Lebenslauf{" "}
                    <span style={{ color: "#b0b8d4", fontSize: "0.85rem" }}>(optional · PDF · max. {MAX_FILE_SIZE_MB} MB)</span>
                  </Form.Label>
                  <div className="kontakt-file-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="lebenslauf"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      className="kontakt-file-input"
                    />
                    <label htmlFor="lebenslauf" className="kontakt-file-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      {lebenslauf ? lebenslauf.name : "PDF-Datei auswählen…"}
                    </label>
                    {lebenslauf && (
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="kontakt-file-remove"
                        aria-label="Datei entfernen"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {lebenslauf && (
                    <div style={{ marginTop: "6px", fontSize: "0.82rem", color: "#9ae6b4" }}>
                      ✓ {lebenslauf.name} ({(lebenslauf.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </Col>
              </Row>
              <Form.Group controlId="datenschutz" className="mb-4 d-flex align-items-start" style={{ color: "#e3e8ff" }}>
                <Form.Check
                  type="checkbox"
                  name="datenschutz"
                  checked={formData.datenschutz}
                  onChange={handleChange}
                  className="me-2 mt-1"
                  required
                />
                <Form.Label className="mb-0">
                  Ja, ich stimme der{" "}
                  <a href="/Datenschutzerklärung" style={{ color: "#ff8ba7", textDecoration: "none" }}>
                    Datenschutzerklärung
                  </a>{" "}
                  zu.
                </Form.Label>
              </Form.Group>
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: "#111D4B",
                  borderColor: "#111D4B",
                  padding: "12px 28px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  width: "100%",
                }}
              >
                {isSubmitting ? "Senden..." : "Senden"}
              </Button>
              {status.message && (
                <div
                  style={{
                    marginTop: "12px",
                    color: status.type === "success" ? "#9ae6b4" : "#ff8ba7",
                    fontWeight: 500,
                  }}
                >
                  {status.message}
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Kontakt;
